import React, { useRef, useState, useEffect, useCallback } from "react";
import { AnimatePresence } from "framer-motion";
import FilePreview from "@/components/file/Preview";
import { FileCarousel } from "@/components/file/Carousel/FileCarousel";
import { AnnotationEditRequestDetails } from "@utils/annotationStrategies/types";
import { BoundingBox, BoundingBoxStrategyImpl } from "@utils/annotationStrategies/BoundingBox";
import AnnotationLabelInput from "@/components/annotation/AnnotationLabelInput";
import { useCoreApp } from "@/contexts/CoreAppContext";
import { useFlow } from "@/contexts/FlowContext";
import { useFrame } from "@/contexts/FrameContext";
import { useComputedValues } from "@/contexts/ComputedValuesContext";
import { FilePreviewProvider } from "@/contexts/FilePreviewProvider";
import { Annotation } from "@/types/annotation";
import { useImproveContext } from "@/contexts/ImproveContext";
import {
    useCurrentMedia,
    usePromptLabels,
    useMarkLabelAsEdited,
    useUpdatePromptLabels,
    useSyncPromptLabels,
    useImproveLoading,
    useImproveError,
    useLimitedSources,
    useApprovedImageIds
} from "@/store";
import { MediaItemStatus } from "@/types/media";
import { DEFAULT_LABEL } from "@/components/annotation/colors";
import { ErrorInfo } from "@/components/ErrorInfo";
import { LoadingOverlay } from "@/components/file/Preview/Loading";
import { useAnnotationSync } from "@/hooks/useAnnotationSync";

export const ImproveVisualAreaContent = () => {
    const currentMedia = useCurrentMedia();
    const { showLabels, setPreviouslySelectedObject } = useCoreApp();
    const { setCurrentFrameAnnotations } = useFlow();
    const {
        selectedAnnotationId,
        setSelectedAnnotationId,
        shouldShowAnnotationInput,
        setShouldShowAnnotationInput
    } = useFrame();
    const { currentFrameAnnotations, allAnnotationsAcrossFiles } = useComputedValues();
    const promptLabels = usePromptLabels();
    const markLabelAsEdited = useMarkLabelAsEdited();
    const updatePromptLabels = useUpdatePromptLabels();
    const syncPromptLabels = useSyncPromptLabels();

    // State from Zustand store
    const loading = useImproveLoading();
    const error = useImproveError();
    const sources = useLimitedSources();
    const approvedImageIds = useApprovedImageIds();

    // Orchestration methods from context
    const { loadImproveBatch, handleAddImageForReview, addingImageStatus } = useImproveContext();

    // Annotation sync hook for persisting changes to backend
    // Using queueSync for centralized queue that persists across image changes
    const { queueSync } = useAnnotationSync({ debug: true });

    const visualAreaContainerRef = useRef<HTMLDivElement>(null);

    const [editingAnnotationDetails, setEditingAnnotationDetails] =
        useState<AnnotationEditRequestDetails<BoundingBox> | null>(null);
    const pendingLabelRef = useRef<{ annotationId: string; label: string } | null>(null);

    // Track if initial annotations have been loaded (to avoid syncing on first load)
    // Using a Map to track per-file state since refs get reset on component remount
    const initialLoadCompleteRef = useRef<Map<string, boolean>>(new Map());
    const previousAnnotationsRef = useRef<Map<string, string>>(new Map());

    // Watch for annotation changes and queue sync via centralized queue
    useEffect(() => {
        const fileId = currentMedia?.file?.id;

        // Skip if we're still loading or no annotations yet
        if (loading || !fileId) {
            return;
        }

        // Build a comparable JSON string of current annotations
        const currentJson = JSON.stringify(
            currentFrameAnnotations.map((a) => ({
                id: a.id,
                label: a.label,
                x: a.x,
                y: a.y,
                width: a.width,
                height: a.height
            }))
        );

        const isInitialLoadComplete = initialLoadCompleteRef.current.get(fileId) ?? false;
        const previousJson = previousAnnotationsRef.current.get(fileId) ?? "";

        // Skip the very first load (initial annotations from source)
        if (!isInitialLoadComplete) {
            if (currentFrameAnnotations.length > 0) {
                console.log("[ImproveVisualAreaContent] Initial load complete for file:", fileId);
                initialLoadCompleteRef.current.set(fileId, true);
                previousAnnotationsRef.current.set(fileId, currentJson);
            }
            return;
        }

        // Skip if annotations haven't actually changed
        if (currentJson === previousJson) {
            return;
        }

        // Update previous annotations ref
        previousAnnotationsRef.current.set(fileId, currentJson);

        // Filter out empty annotations before syncing
        const annotationsToSync = currentFrameAnnotations.filter(
            (a) => !a.empty && a.label && a.label !== DEFAULT_LABEL
        );

        if (annotationsToSync.length === 0) {
            console.log("[ImproveVisualAreaContent] No annotations to sync, skipping");
            return;
        }

        console.log(
            "[ImproveVisualAreaContent] Queueing sync for",
            annotationsToSync.length,
            "annotations"
        );
        // Queue sync via centralized queue (handles debouncing and persists across image changes)
        queueSync(annotationsToSync);
    }, [currentFrameAnnotations, currentMedia?.file?.id, loading, queueSync]);

    // Note: No need to cancel sync on image change - the centralized queue handles this
    // and syncs will continue in the background even after navigating away

    const onFileChange = useCallback(() => {
        setEditingAnnotationDetails(null);
        pendingLabelRef.current = null;
    }, []);

    const handleAnnotationEditRequest = useCallback(
        (details: AnnotationEditRequestDetails<BoundingBox>) => {
            setEditingAnnotationDetails(details);
        },
        []
    );

    const getAnnotationLabel = useCallback(
        (annotationId: string) => {
            return currentFrameAnnotations.find((anno) => anno.id === annotationId)?.label;
        },
        [currentFrameAnnotations]
    );

    const cleanupDefaultLabel = useCallback(
        (annotationId: string, oldLabel: string | undefined, newLabel: string) => {
            if (oldLabel === DEFAULT_LABEL && oldLabel !== newLabel) {
                const hasOtherAnnotationsWithDefaultLabel = allAnnotationsAcrossFiles.some(
                    (anno) => anno.label === DEFAULT_LABEL && anno.id !== annotationId
                );

                if (!hasOtherAnnotationsWithDefaultLabel) {
                    const remainingLabelNames = Array.from(promptLabels.keys()).filter(
                        (labelName) => labelName !== DEFAULT_LABEL
                    );

                    updatePromptLabels([], [DEFAULT_LABEL]);

                    setTimeout(() => {
                        syncPromptLabels([...remainingLabelNames, newLabel]);
                    }, 0);
                }
            }
        },
        [allAnnotationsAcrossFiles, promptLabels, updatePromptLabels, syncPromptLabels]
    );

    const updateAnnotationLabel = useCallback(
        (annotationId: string, newLabel: string) => {
            const sanitizedLabel = newLabel.trim().toLowerCase();
            if (!sanitizedLabel) return;

            const oldLabel = getAnnotationLabel(annotationId);

            setPreviouslySelectedObject(sanitizedLabel);
            setCurrentFrameAnnotations((prevAnnotations: Annotation[]) =>
                (prevAnnotations || []).map((anno: Annotation) =>
                    anno.id === annotationId
                        ? { ...anno, label: sanitizedLabel, draft: false, modifiedAt: Date.now() }
                        : anno
                )
            );

            markLabelAsEdited(sanitizedLabel);
            cleanupDefaultLabel(annotationId, oldLabel, sanitizedLabel);
        },
        [
            getAnnotationLabel,
            setPreviouslySelectedObject,
            setCurrentFrameAnnotations,
            markLabelAsEdited,
            cleanupDefaultLabel
        ]
    );

    const handleLabelInputClose = useCallback(
        (shouldDiscard: boolean = false) => {
            if (!shouldDiscard && pendingLabelRef.current) {
                const { annotationId, label } = pendingLabelRef.current;
                updateAnnotationLabel(annotationId, label);
            }
            pendingLabelRef.current = null;

            setEditingAnnotationDetails(null);
            setSelectedAnnotationId(null);
            setShouldShowAnnotationInput(false);

            if (editingAnnotationDetails) {
                setCurrentFrameAnnotations((prevAnnotations: Annotation[]) =>
                    (prevAnnotations || []).filter((anno: Annotation) => !anno.empty)
                );
            }
        },
        [
            editingAnnotationDetails,
            setCurrentFrameAnnotations,
            setSelectedAnnotationId,
            setShouldShowAnnotationInput,
            updateAnnotationLabel
        ]
    );

    const handleCanvasClick = useCallback(() => {
        handleLabelInputClose();
    }, [handleLabelInputClose]);

    const handleLabelChangedByInput = useCallback(
        (annotationId: string, newLabel: string) => {
            updateAnnotationLabel(annotationId, newLabel);
        },
        [updateAnnotationLabel]
    );

    const handlePendingLabelChange = useCallback((annotationId: string, newLabel: string) => {
        pendingLabelRef.current = { annotationId, label: newLabel };
    }, []);

    // Add new images: upload, run inference, add to review batch
    const onAddMediaItems = useCallback(
        async (files: File[], onFinally?: () => void) => {
            try {
                await handleAddImageForReview(files);
            } finally {
                onFinally?.();
            }
        },
        [handleAddImageForReview]
    );

    // Show input on label click or thumbnail click
    useEffect(() => {
        if (
            selectedAnnotationId &&
            shouldShowAnnotationInput &&
            currentMedia?.frame?.id &&
            visualAreaContainerRef.current &&
            !editingAnnotationDetails
        ) {
            const selectedAnnotation = currentFrameAnnotations.find(
                (anno: BoundingBox) => anno.id === selectedAnnotationId
            );

            if (selectedAnnotation) {
                const canvasElement = visualAreaContainerRef.current.querySelector(
                    "[data-annotation-canvas]"
                ) as HTMLCanvasElement;
                const canvasBoundingRect =
                    canvasElement?.getBoundingClientRect() ||
                    visualAreaContainerRef.current.getBoundingClientRect();

                const boxTlxCanvas = selectedAnnotation.x - selectedAnnotation.width / 2;
                const boxTlyCanvas = selectedAnnotation.y - selectedAnnotation.height / 2;

                const editDetails: AnnotationEditRequestDetails<BoundingBox> = {
                    annotation: selectedAnnotation,
                    boxCanvasTop: boxTlyCanvas,
                    boxCanvasLeft: boxTlxCanvas,
                    canvasBoundingRect: canvasBoundingRect
                };

                setEditingAnnotationDetails(editDetails);
            }
        }
    }, [
        selectedAnnotationId,
        shouldShowAnnotationInput,
        currentMedia?.frame?.id,
        currentFrameAnnotations,
        editingAnnotationDetails
    ]);

    // Show error state
    if (error) {
        return (
            <div className="flex h-full items-center justify-center p-4">
                <ErrorInfo error={error} onRetryClick={() => loadImproveBatch()} />
            </div>
        );
    }

    // Note: No custom empty state - FileCarousel shows FileInputArea when no files
    return (
        <FilePreviewProvider onFileChange={onFileChange}>
            <AnimatePresence mode="wait">
                <div ref={visualAreaContainerRef} className="visualArea">
                    <div className="flex min-h-0 flex-1 flex-col">
                        <FileCarousel
                            onAddMediaItems={onAddMediaItems}
                            isImproveMode={true}
                            approvedImageIds={approvedImageIds}
                        >
                            <div className="relative flex min-h-0 flex-1 flex-col">
                                <FilePreview
                                    key={`file-${currentMedia?.file.id}`}
                                    strategy={BoundingBoxStrategyImpl}
                                    forceHideLabels={!showLabels}
                                    editingAnnotationId={
                                        editingAnnotationDetails?.annotation.id || null
                                    }
                                    onAnnotationEditRequest={handleAnnotationEditRequest}
                                    onCanvasClickWithoutAnnotation={handleCanvasClick}
                                />
                                <LoadingOverlay
                                    show={currentMedia?.file?.status === MediaItemStatus.UPLOADING}
                                    loadingText={addingImageStatus || "Processing..."}
                                />
                            </div>
                        </FileCarousel>
                    </div>

                    {editingAnnotationDetails && visualAreaContainerRef.current && (
                        <AnnotationLabelInput
                            key={`inner-input-${editingAnnotationDetails.annotation.id}`}
                            annotationEditRequestDetails={editingAnnotationDetails}
                            containerRef={visualAreaContainerRef as React.RefObject<HTMLDivElement>}
                            onLabelChange={handleLabelChangedByInput}
                            onPendingLabelChange={handlePendingLabelChange}
                            onClose={handleLabelInputClose}
                        />
                    )}
                </div>
            </AnimatePresence>
        </FilePreviewProvider>
    );
};
